/**
 * Checkout Store (Zustand)
 *
 * Manages checkout state including:
 * - Current step
 * - Shipping address
 * - Order information
 * - Processing state
 *
 * Country code is sourced from the country feature store.
 * Use useCheckoutCountry() hook to get the current country.
 *
 * @file useCheckoutStore.ts
 */

import {create} from 'zustand';
import {devtools, persist} from 'zustand/middleware';
import type {CheckoutStep, ShippingAddress} from '../types/checkout.types';
import type {ValidateDiscountResponse} from '../types/discount.types';
import {useCountryCode} from '@/features/country';

// Shipping method selection
interface ShippingMethodSelection {
  id: string;
  name: string;
  value: string;
  priceMinorUnits: number;
  priceDisplay: string;
}

interface CheckoutState {
  // Current step
  step: CheckoutStep;

  // Form data
  shippingAddress: ShippingAddress | null;
  billingAddressSameAsShipping: boolean;

  // Shipping method
  shippingMethod: ShippingMethodSelection | null;

  // Order info
  orderId: string | null;
  orderNumber: string | null;

  // Processing state
  isProcessing: boolean;
  error: string | null;

  // Payment
  paymentSessionId: string | null;

  // Discount
  discountCode: string | null;
  appliedDiscount: ValidateDiscountResponse | null;
}

interface CheckoutActions {
  // Navigation
  setStep: (step: CheckoutStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Shipping
  setShippingAddress: (address: ShippingAddress) => void;
  setBillingAddressSameAsShipping: (same: boolean) => void;
  setShippingMethod: (method: ShippingMethodSelection | null) => void;

  // Order
  setOrderId: (id: string) => void;
  setOrderNumber: (number: string) => void;

  // Processing
  setProcessing: (isProcessing: boolean) => void;
  setError: (error: string | null) => void;

  // Payment
  setPaymentSessionId: (sessionId: string) => void;

  // Discount
  setDiscountCode: (code: string | null) => void;
  setAppliedDiscount: (discount: ValidateDiscountResponse | null) => void;
  clearDiscount: () => void;

  // Reset
  reset: () => void;
}

const initialState: CheckoutState = {
  step: 'shipping',
  shippingAddress: null,
  billingAddressSameAsShipping: true,
  shippingMethod: null,
  orderId: null,
  orderNumber: null,
  isProcessing: false,
  error: null,
  paymentSessionId: null,
  discountCode: null,
  appliedDiscount: null,
};

const STEP_ORDER: CheckoutStep[] = ['shipping', 'review', 'payment', 'processing', 'complete'];

export const useCheckoutStore = create<CheckoutState & CheckoutActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Navigation
        setStep: (step) => set({ step, error: null }, false, 'setStep'),

        nextStep: () => {
          const currentIndex = STEP_ORDER.indexOf(get().step);
          const nextStep = STEP_ORDER[currentIndex + 1];
          if (currentIndex < STEP_ORDER.length - 1 && nextStep) {
            set({ step: nextStep, error: null }, false, 'nextStep');
          }
        },

        prevStep: () => {
          const currentIndex = STEP_ORDER.indexOf(get().step);
          const prevStep = STEP_ORDER[currentIndex - 1];
          if (currentIndex > 0 && prevStep) {
            set({ step: prevStep, error: null }, false, 'prevStep');
          }
        },

        // Shipping
        setShippingAddress: (address) =>
          set({ shippingAddress: address }, false, 'setShippingAddress'),

        setBillingAddressSameAsShipping: (same) =>
          set({ billingAddressSameAsShipping: same }, false, 'setBillingAddressSameAsShipping'),

        setShippingMethod: (method: ShippingMethodSelection | null) =>
          set({ shippingMethod: method }, false, 'setShippingMethod'),

        // Order
        setOrderId: (id) => set({ orderId: id }, false, 'setOrderId'),
        setOrderNumber: (number) => set({ orderNumber: number }, false, 'setOrderNumber'),

        // Processing
        setProcessing: (isProcessing) => set({ isProcessing }, false, 'setProcessing'),
        setError: (error) => set({ error, isProcessing: false }, false, 'setError'),

        // Payment
        setPaymentSessionId: (sessionId) =>
          set({ paymentSessionId: sessionId }, false, 'setPaymentSessionId'),

        // Discount
        setDiscountCode: (code) =>
          set({ discountCode: code }, false, 'setDiscountCode'),
        setAppliedDiscount: (discount) =>
          set({ appliedDiscount: discount, discountCode: discount?.code ?? null }, false, 'setAppliedDiscount'),
        clearDiscount: () =>
          set({ discountCode: null, appliedDiscount: null }, false, 'clearDiscount'),

        // Reset
        reset: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'tangi-checkout',
        partialize: (state) => ({
          shippingAddress: state.shippingAddress,
          billingAddressSameAsShipping: state.billingAddressSameAsShipping,
          shippingMethod: state.shippingMethod,
        }),
      }
    ),
    { name: 'checkout-store' }
  )
);

// ============================================
// COUNTRY INTEGRATION
// ============================================

/**
 * Hook for getting country code in checkout
 *
 * Uses country feature store as the single source of truth.
 *
 * @returns Country code string (defaults to 'SE' if not set)
 */
export function useCheckoutCountry(): string {
  const countryCode = useCountryCode();
  return countryCode ?? 'SE';
}
